import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { AppContext, Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { useNextChallenge } from "./use-next-challenge";
import { useSessionResultsHeaderButton } from "./use-session-results-header-button";
import { useSessionResultsRequest } from "./use-session-results-request";
import { type IUseSessionResults, type SessionNavigationData, type SessionResultsPageModel } from "./use-session-results.types";

export type {
  IPerformanceStats,
  IScenarioData,
  SessionResultsData,
  SessionResultsPageModel,
  SessionResultsStaticModel,
  SessionResultsTabModel,
  SessionResultsScenarioModel,
  SessionResultsCombinedModel,
} from "./use-session-results.types";

export const useSessionResults = ({ headerButtonType = VRHeaderButtonType.BACK }: IUseSessionResults): SessionResultsPageModel => {
  const { state } = useContext(AppContext);
  const { sessionId, challengeId } = useParams();
  const location = useLocation();

  const sessionData = (location.state as SessionNavigationData | null) ?? null;

  const sessionResults = useSessionResultsRequest(sessionId);
  const [currentTab, setCurrentTab] = useState(0);
  const userId = state.user.id ?? null;

  const { nextChallenge, showNextChallengeCta } = useNextChallenge({
    sessionData,
    challengeId,
    userId: userId ?? undefined,
    userRating: state.user.userRating,
  });

  const handleNextChallengeClick = useCallback(() => {
    if (!nextChallenge) {
      return;
    }

    Events.app.navigate.emit({
      path: `/challenges/challenge/${nextChallenge.id}`,
      data: nextChallenge,
      resetTo: "/challenges",
    });
  }, [nextChallenge]);

  const handleContinueClick = useCallback(() => {
    if (nextChallenge) {
      handleNextChallengeClick();
      return;
    }

    Events.app.navigateBack.emit();
  }, [handleNextChallengeClick, nextChallenge]);

  useSessionResultsHeaderButton({
    headerButtonType,
    onContinueClick: handleContinueClick,
  });

  useEffect(() => {
    if (!sessionResults) {
      return;
    }

    setCurrentTab(0);
  }, [sessionResults?.cognitive_result_id]);

  const staticModel = useMemo(
    () => ({
      userId,
      sessionData,
      sessionResults,
      nextChallenge,
      showNextChallengeCta,
      handleNextChallengeClick,
    }),
    [handleNextChallengeClick, nextChallenge, sessionData, sessionResults, showNextChallengeCta, userId]
  );

  const tabModel = useMemo(
    () => ({
      currentTab,
      setCurrentTab,
    }),
    [currentTab]
  );

  return useMemo(
    () => ({
      staticModel,
      tabModel,
    }),
    [staticModel, tabModel]
  );
};
